"use client";


import { ChartSpline } from "lucide-react";
import { memo, useEffect, useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getBookDisplayName } from "@/lib/utils/sports-books";

export interface LineHistoryPoint {
  ts: string;
  line: number;
  price_american: number;
  price_decimal?: number;
  liquidity?: number | null;
  change_kind?: string;
}

export interface PairedCell {
  line: number;
  price_american: number;
  price_decimal: number;
  liquidity: number | null;
  change_kind: string;
}

export interface PairedRow {
  ts: string;
  a: PairedCell | null;
  b: PairedCell | null;
}

export interface PairedSide {
  side: string;
  selection_key: string;
}

export interface PairedSides {
  a: PairedSide;
  b: PairedSide | null;
}

export interface PairedTimeline {
  rows: PairedRow[];
  sides: PairedSides;
  line: number;
}

export interface ClosingLineSnapshot {
  line: number;
  price_american: number;
  price_decimal: number;
  captured_at: string;
  capture_kind: string;
}

export interface PairedClosing {
  a: ClosingLineSnapshot | null;
  b: ClosingLineSnapshot | null;
}

export interface OpeningLineSnapshot {
  line: number;
  price_american: number;
  price_decimal: number;
  observed_at: string;
}

export interface PairedOpening {
  a: OpeningLineSnapshot | null;
  b: OpeningLineSnapshot | null;
}

export interface LineHistoryPairedData extends PairedTimeline {
  event_id: string;
  view: "paired";
  since: string;
  until: string;
  event_start: string | null;
  effective_since: string;
  truncated: boolean;
  closing?: PairedClosing | null;
  opening?: PairedOpening | null;
}

export interface LineHistoryPairedResponse {
  data?: LineHistoryPairedData;
  error?: string;
  meta?: {
    response_ms?: number;
    truncated?: boolean;
  };
}

export interface LineHistorySeries {
  book: string;
  selection_key: string;
  market_key: string;
  entity_name?: string;
  entity_name_std?: string;
  side: string;
  is_alt?: boolean;
  points: LineHistoryPoint[];
}

interface LineHistoryResponse {
  data?: {
    event_id: string;
    series?: LineHistorySeries[];
    truncated?: boolean;
  };
  error?: string;
}

const CACHE_TTL_MS = 60_000;
const CACHE_MAX_ENTRIES = 50;
const MAX_SELECTION_KEYS = 64;
const seriesCache = new Map<
  string,
  { fetchedAt: number; promise: Promise<LineHistorySeries[]> }
>();

export function fetchRowHistory(
  eventId: string,
  selectionKeys: string[],
  since?: string,
): Promise<LineHistorySeries[]> {
  const keys = [...new Set(selectionKeys)].slice(0, MAX_SELECTION_KEYS).sort();
  const cacheKey = `${eventId}|${keys.join(",")}`;
  const cached = seriesCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.promise;
  }
  const params = new URLSearchParams({ event_id: eventId });
  if (keys.length > 0) {
    params.set("selections", keys.join(","));
  }
  if (since) {
    const sinceMs = Date.parse(since);
    if (Number.isFinite(sinceMs) && sinceMs <= Date.now()) {
      params.set("since", since);
    }
  }
  const promise = fetch(`/api/linehistory?${params.toString()}`, {
    headers: { Accept: "application/json" },
  })
    .then(async (res) => {
      if (!res.ok) {
        seriesCache.delete(cacheKey);
        return [] as LineHistorySeries[];
      }
      const body = (await res.json()) as LineHistoryResponse;
      return body.data?.series ?? [];
    })
    .catch(() => {
      seriesCache.delete(cacheKey);
      return [] as LineHistorySeries[];
    });

  seriesCache.delete(cacheKey);
  seriesCache.set(cacheKey, { fetchedAt: Date.now(), promise });
  if (seriesCache.size > CACHE_MAX_ENTRIES) {
    const oldest = seriesCache.keys().next().value;
    if (oldest !== undefined) {
      seriesCache.delete(oldest);
    }
  }
  return promise;
}

export function __clearLineHistoryCache(): void {
  seriesCache.clear();
}

export function dropEventLineHistory(eventId: string): void {
  for (const key of seriesCache.keys()) {
    if (key.startsWith(`${eventId}|`)) seriesCache.delete(key);
  }
}

export function useEventLineHistory(
  eventId: string,
  enabled: boolean,
  eventStart: Date | null | undefined,
  selectionKeys: string[],
): LineHistorySeries[] | null {
  const [state, setState] = useState<{
    eventId: string;
    requestKey: string;
    series: LineHistorySeries[];
  } | null>(null);
  const sinceIso =
    eventStart && !Number.isNaN(eventStart.getTime())
      ? new Date(eventStart.getTime() - 48 * 3600 * 1000).toISOString()
      : undefined;
  const keysJoined = [...new Set(selectionKeys)]
    .slice(0, MAX_SELECTION_KEYS)
    .sort()
    .join(",");
  const requestKey = `${eventId}|${keysJoined}`;

  useEffect(() => {
    if (!enabled || !eventId || keysJoined === "") {
      return;
    }
    if (state && state.requestKey === requestKey) {
      return;
    }
    let cancelled = false;
    fetchRowHistory(eventId, keysJoined.split(","), sinceIso).then(
      (result) => {
        if (!cancelled) {
          setState({ eventId, requestKey, series: result });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, eventId, sinceIso, keysJoined, requestKey, state]);

  return state && state.eventId === eventId ? state.series : null;
}

function normalizeLabel(value: string | undefined | null): string {
  return (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function findBookSeries(
  series: LineHistorySeries[] | null,
  opts: {
    book: string;
    selectionId?: string;
    side: string;
    line?: number;
    entity?: string;
    marketLabels?: Array<string | undefined>;
  },
): LineHistorySeries | null {
  if (!series || series.length === 0) {
    return null;
  }
  const side = opts.side.toLowerCase();
  const book = opts.book.toLowerCase();

  if (opts.selectionId) {
    const key = `${opts.selectionId}|${side}`;
    const exact = series.find(
      (s) => s.book === book && s.selection_key === key,
    );
    if (exact) {
      return exact;
    }
  }

  const marketSlugs = (opts.marketLabels ?? [])
    .map((label) => normalizeLabel(label))
    .filter((slug) => slug !== "");
  if (marketSlugs.length === 0) {
    return null;
  }
  const entitySlug = normalizeLabel(opts.entity);
  const candidates = series.filter((s) => {
    if (s.book !== book || s.side.toLowerCase() !== side) {
      return false;
    }
    if (!marketSlugs.includes(normalizeLabel(s.market_key))) {
      return false;
    }
    if (opts.line !== undefined && s.points.length > 0) {
      const lastLine = s.points[s.points.length - 1].line;
      if (Math.abs(lastLine - opts.line) > 0.001) {
        return false;
      }
    }
    if (entitySlug) {
      const seriesEntity =
        normalizeLabel(s.entity_name_std) || normalizeLabel(s.entity_name);
      if (seriesEntity !== entitySlug) {
        return false;
      }
    }
    return true;
  });
  return candidates.length === 1 ? candidates[0] : null;
}

export function canOpenBookHistory(
  series: LineHistorySeries | null | undefined,
  opts: { book: string; selectionId: string | null | undefined; side: string },
): series is LineHistorySeries {
  if (!series || !opts.selectionId) {
    return false;
  }
  if (series.book !== opts.book.toLowerCase()) {
    return false;
  }
  if (series.selection_key !== `${opts.selectionId}|${opts.side.toLowerCase()}`) {
    return false;
  }
  return series.points.some((p) => p.change_kind !== "removed");
}

interface LineSparklineProps {
  series: LineHistorySeries | null;
  width?: number;
  height?: number;
  className?: string;
  onOpen?: () => void;
}

const MIN_SPAN_CENTS = 20;

const restingChip =
  "inline-flex items-center rounded-md border border-white/[0.08] bg-white/[0.04] px-1 py-0.5 shadow-xs backdrop-blur-xs";
const openerChip = `${restingChip} cursor-pointer transition-colors hover:bg-white/[0.08] hover:border-white/[0.15] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`;

export const LineSparkline = memo(function LineSparkline({
  series,
  width = 56,
  height = 18,
  className,
  onOpen,
}: LineSparklineProps) {
  if (!series) {
    return null;
  }
  const points = series.points.filter((p) => p.change_kind !== "removed");
  if (points.length < 2) {
    if (!onOpen || points.length === 0) {
      return null;
    }
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Show line history for ${getBookDisplayName(series.book)}`}
              onClick={(event) => {
                event.stopPropagation();
                onOpen();
              }}
              className={`${openerChip} ${className ?? ""}`}
            >
              <ChartSpline
                aria-hidden
                className="size-3.5 text-muted-foreground"
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6}>
            Line history
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const cents = points.map((p) =>
    p.price_american > 0 ? p.price_american - 100 : p.price_american + 100,
  );
  const minY = Math.min(...cents);
  const maxY = Math.max(...cents);
  const rawSpan = maxY - minY;
  const span = Math.max(rawSpan, MIN_SPAN_CENTS);
  const mid = (minY + maxY) / 2;
  const domainMin = mid - span / 2;
  const pad = 2;
  const stepX = (width - pad * 2) / (cents.length - 1);
  const coords = cents.map((value, idx) => {
    const x = pad + idx * stepX;
    const y = pad + (height - pad * 2) * (1 - (value - domainMin) / span);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const open = points[0].price_american;
  const close = points[points.length - 1].price_american;
  const delta = cents[cents.length - 1] - cents[0];
  const stroke =
    Math.abs(delta) <= 2
      ? "stroke-zinc-500"
      : delta < 0
        ? "stroke-accent-value"
        : "stroke-amber-400";

  const fmt = (odds: number) => (odds > 0 ? `+${odds}` : `${odds}`);

  const chart = (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Price moved from ${fmt(open)} to ${fmt(close)}`}
    >
      <polyline
        points={coords.join(" ")}
        fill="none"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={stroke}
      />
    </svg>
  );

  if (onOpen) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Show line history for ${getBookDisplayName(series.book)}`}
              onClick={(event) => {
                event.stopPropagation();
                onOpen();
              }}
              className={`${openerChip} ${className ?? ""}`}
            >
              {chart}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6}>
            Line history
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <span
      className={`${restingChip} ${className ?? ""}`}
      title={`Line movement: opened ${fmt(open)}, now ${fmt(close)} (${points.length} points)`}
    >
      {chart}
    </span>
  );
});
